import React, { useState, useMemo } from 'react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Users, CheckCircle, Target, Package, AlertCircle, ArrowLeft, X, Calendar, Mail, Globe, Building, Briefcase, UserCheck, Hash, Info } from 'lucide-react';
import PaginationBar from '../shared/PaginationBar';
import { useLeadRequests } from '../../hooks/useSupabaseLeads';
import { formatInTz } from '../../utils/dateFormatters';
import { useTimezone } from '../../hooks/useTimezone';
import { supabase } from '../../lib/supabase';
import { getLocalUser } from '../../utils/authHelpers';
import StatsCard from '../Dashboard/StatsCard';
import FeedbackPopup from '../FeedbackPopup';
import SimpleCustomSelect from '../shared/SimpleCustomSelect';
import LoadingScreen from '../shared/LoadingScreen';

const LeadRequestsStandalonePage: React.FC = () => {
  const tz = useTimezone();
  const navigate = useNavigate();
  const { data: allRequests, loading: isLoading, error } = useLeadRequests();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [personFilter, setPersonFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [userNameMap, setUserNameMap] = useState<Record<string, string>>({});
  const itemsPerPage = 10;

  useEffect(() => {
    const localUser = getLocalUser();
    if (!localUser?.client_id) return;
    supabase
      .from('user_settings')
      .select('user_id, full_name')
      .eq('client_id', localUser.client_id)
      .then(({ data }) => {
        if (!data) return;
        const map: Record<string, string> = {};
        for (const row of data) {
          if (row.user_id && row.full_name) map[row.user_id] = row.full_name;
        }
        setUserNameMap(map);
      });
  }, []);

  const users = useMemo(() => {
    const emails = new Set<string>();
    allRequests.forEach(r => { if (r.user_id) emails.add(r.user_id); });
    return Array.from(emails).sort();
  }, [allRequests]);

  const filteredRequests = allRequests.filter(request => {
    const matchesSearch = (request.country || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (request.industry || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (request.request_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (request.dm_titles || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    const matchesPerson = personFilter === 'all' || request.user_id === personFilter;
    return matchesSearch && matchesStatus && matchesPerson;
  }).sort((a, b) => {
    const dateA = new Date(a.created_at || 0);
    const dateB = new Date(b.created_at || 0);
    return dateB.getTime() - dateA.getTime();
  });

  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const requests = filteredRequests.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, personFilter]);

  const totalRequestsSubmitted = allRequests.length;
  const totalRequestsDelivered = allRequests.filter(request => request.status === 'delivered' || request.status === 'partially_delivered').length;
  const totalLeadsRequestedQuantity = allRequests.reduce((sum, request) => sum + (parseInt(request.quantity_requested) || 0), 0);
  const totalLeadsDeliveredQuantity = allRequests.reduce((sum, request) => sum + (parseInt(request.quantity_delivered) || 0), 0);

  if (isLoading && allRequests.length === 0) {
    return <LoadingScreen />;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'bg-[#05E0D1]/10 text-[#05E0D1] border-[#05E0D1]/30';
      case 'partially_delivered':
        return 'bg-amber-50 text-amber-700 border-amber-300';
      case 'in_progress':
        return 'bg-[#1875FF]/10 text-[#1875FF] border-[#1875FF]/30';
      case 'pending':
        return 'bg-[#1875FF]/5 text-[#1875FF]/70 border-[#1875FF]/20';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'in_progress':
        return 'In Progress';
      case 'delivered':
        return 'Delivered';
      case 'partially_delivered':
        return 'Partially Delivered';
      case 'pending':
        return 'Pending';
      default:
        return status;
    }
  };

  const formatCompanySizeCondensed = (ranges: string | undefined, headcount: string | undefined) => {
    const data = ranges || headcount;
    if (!data) return 'N/A';

    const rangeArray = data.split(',').map(r => r.trim());
    if (rangeArray.length === 0) return 'N/A';
    if (rangeArray.length === 1) return rangeArray[0];

    return `${rangeArray[0]} - ${rangeArray[rangeArray.length - 1]}`;
  };

  const formatCompanySizeFull = (ranges: string | undefined, headcount: string | undefined) => {
    return ranges || headcount || 'N/A';
  };

  const getUserInfo = (email: string | null | undefined) => {
    const displayName = (email && userNameMap[email]) || email || 'System';
    return {
      name: displayName,
      color: 'bg-gradient-to-r from-[#1875FF]/15 to-[#05E0D1]/15 text-[#1875FF] border-[#1875FF]/40'
    };
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/ai-agents/lead-gen')}
          className="flex items-center text-gray-600 hover:text-[#1875FF] transition-colors font-medium"
        >
          <ArrowLeft size={20} className="mr-1" />
          Back to Lead Gen Agent
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-800 p-4 rounded-xl flex items-center space-x-2 shadow-sm">
          <AlertCircle size={20} />
          <span className="font-medium">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Requests Submitted"
          value={totalRequestsSubmitted}
          icon={Users}
          color="blue"
        />
        <StatsCard
          title="Requests Delivered"
          value={totalRequestsDelivered}
          icon={CheckCircle}
          color="green"
        />
        <StatsCard
          title="Leads Requested"
          value={totalLeadsRequestedQuantity}
          icon={Target}
          color="purple"
        />
        <StatsCard
          title="Leads Delivered"
          value={totalLeadsDeliveredQuantity}
          icon={Package}
          color="orange"
        />
      </div>

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-5 shadow-lg">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
              <input
                type="text"
                placeholder="Search requests..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1875FF] focus:border-[#1875FF] transition-all font-medium"
              />
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Filter size={16} className="text-gray-500" />
            <SimpleCustomSelect
              value={personFilter}
              onChange={setPersonFilter}
              options={[
                { value: 'all', label: 'All Users' },
                ...users.map(email => ({ value: email, label: getUserInfo(email).name }))
              ]}
              placeholder="All Users"
              className="w-48"
            />
            <SimpleCustomSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'pending', label: 'Pending' },
                { value: 'in_progress', label: 'In Progress' },
                { value: 'delivered', label: 'Delivered' },
                { value: 'partially_delivered', label: 'Partially Delivered' }
              ]}
              placeholder="All Status"
              className="w-48"
            />
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 shadow-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-max">
            <thead className="bg-gradient-to-r from-[#05E0D1]/10 to-[#1875FF]/10 border-b-2 border-[#1875FF]/10">
              <tr>
                <th className="text-center px-4 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-32">
                  Date
                </th>
                <th className="text-center px-4 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-40">
                  User
                </th>
                <th className="text-center px-4 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-32">
                  Country
                </th>
                <th className="text-center px-4 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-48">
                  Industry
                </th>
                <th className="text-center px-4 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-40">
                  Company Size
                </th>
                <th className="text-center px-4 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-48">
                  Titles
                </th>
                <th className="text-center px-4 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-40">
                  Quantity
                </th>
                <th className="text-center px-4 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-32">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1875FF]/10">
              {requests.map((request) => (
                <tr
                  key={request.request_id || request.id}
                  className="hover:bg-[#1875FF]/5 transition-colors cursor-pointer"
                  onClick={() => setSelectedRequest(request)}
                >
                  <td className="px-4 py-4 whitespace-nowrap text-center">
                    <div className="text-sm text-gray-900 font-medium">
                      {request.created_at ? formatInTz(new Date(request.created_at), 'MMM dd, yyyy', tz) : 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {request.created_at ? formatInTz(new Date(request.created_at), 'HH:mm', tz) : ''}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold border-2 ${getUserInfo(request.user_id).color}`}>
                      {getUserInfo(request.user_id).name}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <div className="text-sm text-gray-900">{request.country || 'N/A'}</div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <div className="text-sm text-gray-900">{request.industry || 'N/A'}</div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <div className="text-sm text-gray-900">{formatCompanySizeCondensed(request.employee_ranges, request.employee_headcount)}</div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <div className="text-sm text-gray-900 line-clamp-2">{request.dm_titles || 'N/A'}</div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <div className="text-sm font-semibold text-gray-900">
                      {request.quantity_requested || 0} / {request.quantity_delivered || 0}
                    </div>
                    <div className="text-xs text-gray-500">
                      req / del
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border-2 ${getStatusColor(request.status)}`}>
                      {getStatusText(request.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredRequests.length === 0 && !isLoading && (
        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-12 text-center shadow-xl">
          <div className="text-gray-700 text-lg font-extrabold font-display">No requests found</div>
          <div className="text-gray-600 text-sm mt-1 font-medium">
            {searchTerm || statusFilter !== 'all' || personFilter !== 'all' ? 'Try adjusting your filters' : 'No lead requests have been submitted yet'}
          </div>
        </div>
      )}

      <PaginationBar
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalItems={filteredRequests.length}
        startIndex={startIndex}
        itemsPerPage={itemsPerPage}
      />

      {selectedRequest && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="bg-gradient-to-r from-[#05E0D1] to-[#1875FF] p-4 flex items-center justify-between rounded-t-2xl flex-shrink-0">
              <div>
                <h2 className="text-xl font-extrabold text-white font-display">Request Details</h2>
                <p className="text-white/80 text-xs mt-0.5">ID: {selectedRequest.request_id || selectedRequest.id}</p>
              </div>
              <button
                onClick={() => setSelectedRequest(null)}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-3 overflow-y-auto">
              {selectedRequest.status === 'partially_delivered' && selectedRequest.status_reason && (
                <div className="bg-[#1875FF]/5 rounded-xl p-3 border-2 border-[#1875FF]/15 shadow-sm">
                  <div className="flex items-start space-x-2">
                    <div className="w-9 h-9 bg-[#1875FF]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Info className="text-[#1875FF]" size={20} />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-[#1875FF]">Partially Delivered</h3>
                      <p className="text-gray-600 text-sm mt-1">{selectedRequest.status_reason}</p>
                      <p className="text-gray-500 text-xs mt-2">To get more leads, create a new request with different criteria (country, industry, titles, or company size).</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white rounded-xl p-3 border-2 border-[#1875FF]/20 shadow-sm">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-9 h-9 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg flex items-center justify-center">
                      <Calendar className="text-[#1875FF]" size={20} />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900">Date & Time</h3>
                  </div>
                  <p className="text-gray-900 font-semibold text-sm">
                    {selectedRequest.created_at ? formatInTz(new Date(selectedRequest.created_at), 'MMM dd, yyyy', tz) : 'N/A'}
                  </p>
                  <p className="text-gray-500 text-xs">
                    {selectedRequest.created_at ? formatInTz(new Date(selectedRequest.created_at), 'h:mm a', tz) : ''}
                  </p>
                </div>

                <div className="bg-white rounded-xl p-3 border-2 border-[#1875FF]/20 shadow-sm">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-9 h-9 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg flex items-center justify-center">
                      <Mail className="text-[#1875FF]" size={20} />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900">User</h3>
                  </div>
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold border-2 ${getUserInfo(selectedRequest.user_id).color}`}>
                    {getUserInfo(selectedRequest.user_id).name}
                  </span>
                </div>

                <div className="bg-white rounded-xl p-3 border-2 border-[#1875FF]/20 shadow-sm">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-9 h-9 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg flex items-center justify-center">
                      <Target className="text-[#1875FF]" size={20} />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900">Status</h3>
                  </div>
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold border-2 ${getStatusColor(selectedRequest.status)}`}>
                    {getStatusText(selectedRequest.status)}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white rounded-xl p-3 border-2 border-[#1875FF]/20 shadow-sm">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-9 h-9 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg flex items-center justify-center">
                      <Globe className="text-[#1875FF]" size={20} />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900">Country</h3>
                  </div>
                  <p className="text-gray-900 font-semibold text-sm">
                    {selectedRequest.country || 'N/A'}
                  </p>
                </div>

                <div className="bg-white rounded-xl p-3 border-2 border-[#1875FF]/20 shadow-sm">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-9 h-9 bg-gradient-to-br from-[#05E0D1]/10 to-[#1875FF]/10 rounded-lg flex items-center justify-center">
                      <Building className="text-[#05E0D1]" size={20} />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900">Industry</h3>
                  </div>
                  <p className="text-gray-900 font-semibold text-sm">
                    {selectedRequest.industry || 'N/A'}
                  </p>
                </div>

                <div className="bg-white rounded-xl p-3 border-2 border-[#1875FF]/20 shadow-sm">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-9 h-9 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg flex items-center justify-center">
                      <Users className="text-[#1875FF]" size={20} />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900">Company Size</h3>
                  </div>
                  <p className="text-gray-900 font-semibold text-sm">
                    {formatCompanySizeFull(selectedRequest.employee_ranges, selectedRequest.employee_headcount)}
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-3 border-2 border-[#05E0D1]/20 shadow-sm">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="w-9 h-9 bg-gradient-to-br from-[#05E0D1]/10 to-[#1875FF]/10 rounded-lg flex items-center justify-center">
                    <UserCheck className="text-[#05E0D1]" size={20} />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900">Decision Maker Titles</h3>
                </div>
                <p className="text-gray-900 text-sm truncate" title={selectedRequest.dm_titles || 'N/A'}>
                  {selectedRequest.dm_titles || 'N/A'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-xl p-3 border-2 border-[#1875FF]/20 shadow-sm">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-9 h-9 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg flex items-center justify-center">
                      <Hash className="text-[#1875FF]" size={20} />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900">Quantity Requested</h3>
                  </div>
                  <p className="text-gray-900 font-bold text-lg">
                    {selectedRequest.quantity_requested || 0}
                  </p>
                </div>

                <div className="bg-white rounded-xl p-3 border-2 border-[#05E0D1]/20 shadow-sm">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-9 h-9 bg-gradient-to-br from-[#05E0D1]/10 to-[#1875FF]/10 rounded-lg flex items-center justify-center">
                      <Hash className="text-[#05E0D1]" size={20} />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900">Quantity Delivered</h3>
                  </div>
                  <p className="text-[#05E0D1] font-bold text-lg">
                    {selectedRequest.quantity_delivered || 0}
                  </p>
                </div>
              </div>

            </div>

            <div className="bg-gray-50 p-3 flex justify-end border-t border-gray-200 rounded-b-2xl flex-shrink-0">
              <button
                onClick={() => setSelectedRequest(null)}
                className="px-6 py-2 bg-gradient-to-r from-[#05E0D1] to-[#1875FF] text-white rounded-xl font-semibold hover:shadow-lg hover:scale-[1.02] transition-all text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <FeedbackPopup isVisible={true} />
    </div>
  );
};

export default LeadRequestsStandalonePage;
